import React, { useRef, useState } from 'react';
import { databaseAPI } from '../services/api';
import { DatabaseBackup } from '../types';
import { Card } from './common/Card';
import { Button } from './common/Button';

interface DatabaseManagerProps {
  listId: string | null;
  onImportComplete?: () => void;
}

const DatabaseManager: React.FC<DatabaseManagerProps> = ({ listId, onImportComplete }) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    setExportLoading(true);
    setMessage(null);
    setError(null);
    try {
      const blob = await databaseAPI.exportDatabase();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `family-organizer-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage('Sauvegarde téléchargée avec succès');
    } catch (err) {
      setError("Erreur lors de l'export de la base de données");
    } finally {
      setExportLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!listId) {
      setError('Sélectionnez une liste avant d\'importer.');
      e.target.value = '';
      return;
    }

    if (!window.confirm('Importer ce fichier remplacera les articles de la liste sélectionnée. Continuer ?')) {
      e.target.value = '';
      return;
    }

    setImportLoading(true);
    setMessage(null);
    setError(null);
    try {
      const text = await file.text();
      const payload: DatabaseBackup = JSON.parse(text);

      if (!payload.data?.groceries || !payload.data?.meals) {
        throw new Error('Format de fichier invalide');
      }

      const response = await databaseAPI.importDatabase(listId, payload);
      if (response.success && response.data) {
        setMessage(
          `Import réussi : ${response.data.groceries} articles, ${response.data.meals} repas`
        );
        onImportComplete?.();
      } else {
        setError(response.message || "Erreur lors de l'import");
      }
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'import");
    } finally {
      setImportLoading(false);
      e.target.value = '';
    }
  };

  return (
    <Card padding="lg" shadow="medium" className="mb-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">💾 Sauvegarde et restauration</h4>

      {message && (
        <div className="bg-green-50 text-success border-l-4 border-success px-4 py-3 rounded-lg mb-4 text-sm">
          ✅ {message}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-danger border-l-4 border-danger px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="mb-6 pb-6 border-b border-gray-200">
        <p className="text-sm font-medium text-gray-700 mb-1">Exporter la base de données</p>
        <p className="text-sm text-gray-500 mb-3">
          Télécharge un fichier JSON avec tous les articles et les repas planifiés.
        </p>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={handleExport}
          disabled={exportLoading}
        >
          {exportLoading ? 'Export en cours...' : '📤 Télécharger la sauvegarde'}
        </Button>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">Importer une sauvegarde</p>
        <p className="text-sm text-gray-500 mb-3">
          Importe les articles depuis un fichier JSON dans la liste sélectionnée.{' '}
          <strong>Remplace les articles de cette liste.</strong>
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleFileChange}
        />
        <Button
          type="button"
          variant="outline"
          size="md"
          onClick={() => fileInputRef.current?.click()}
          disabled={importLoading}
        >
          {importLoading ? 'Import en cours...' : '📥 Importer une sauvegarde'}
        </Button>
      </div>
    </Card>
  );
};

export default DatabaseManager;
